// The scheduling card of the intake call, the round pages and the debrief (lane B5 S12, screens-walks §2.3–§2.5, §2.7,
// §2.8): "Propose slots -> Availability -> Create Outlook event + Teams link". The Coordinator proposes slots inside every
// required attendee's working hours (a conflict rides on the slot it breaks and never hides it), reads the chosen slot's
// day on the free/busy grid with every time in the event's zone first, and schedules it in the Schedule dialog; once
// booked the card is the Outlook event with its Teams link, delivery status and the attendees' responses, rescheduled in
// place ("Updates modify the same Outlook event.") or cancelled with a reason. An invitee answers their own row (Accept ·
// Decline); the Recruiter and the Hiring Manager *Request* a new time; the HR Business Partner reads the values.
//
// The writes are the V1 tools each page already books through — round.*, intake.schedule_session, debrief.schedule /
// reschedule — which since S07 place their meetings through the scheduling engine (its calendar, Teams and room calls,
// conflicts and history): they also issue the round's packet, the intake call's meeting and the debrief's booking that
// the rest of each page and the later steps read. The reads, the answers and the requests are the scheduling tools'.
// The card is a design-system island (`.mc`, under MissionShell's one McRoot: the shell's McPage) placed above the V1
// sections other lanes still own.

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  AttendeeAvailabilityOut, DebriefScheduleOutput, IntakeScheduleSessionOutput, MeetingOut, RoundBookOutput, RoundProposeOutput,
  SchedulingOpenOutput,
} from "@/api/tools";
import { schedulingAvailability, schedulingOpen } from "@/api/tools";
import { callTool, newIdempotencyKey } from "@/api/runtime";
import { errorText, isClientRefusal, useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { McPage } from "@/shell/page";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Availability, Banner, Button, Card, Conflicts, DEBRIEF_ROOM_LABELS, EmptyState, Field, Flex, ITINERARY_LABELS, Input,
  Journey, MeetingEvent, PageHeader, Paragraph, Pill, ROLE_LABELS, RULE_LABELS, Recorded, Responses, SCHEDULING_LABELS, Slots, Stack, Tag, TextLink,
  TimeZone, copyText, formatDateTime, toneOf,
  type ConflictItem, type ItineraryItem, type StatusWord,
} from "@/mc";
import { ReasonCancelDialog, RequestDialog, ScheduleDialog, SlotRescheduleDialog } from "./dialogs";
import { HeldOfflineButton, HeldOfflineDialog, isRequiredParty, type OfflineParty, type OfflineRecord } from "./HeldOffline";
import { itinerary, responses, syncOf, withTeams } from "./RequestSlideOver";
import {
  HELD_OFFLINE_TEMPLATES, INTAKE_SCHEDULING_LABELS, RESCHEDULE_REQUEST_LABELS, RESCHEDULE_REQUEST_TEMPLATES, SCHEDULING_JOURNEY_LABELS,
} from "./labels";
import {
  blockingAt, causeOf, dayGrid, meetingsOf, openInput, openKey, proposedCount, sequenceWindow, slotItems, windowOf, type ProposedTime, type ScheduleSubject,
} from "./schedule";
import { ROLE_WORD, acts, conflictWord, formatWord, hsId, statusWord, templateWord } from "./workbench";

type Open = "schedule" | "reschedule" | "cancel" | "request" | null;

interface Proposal {
  slots: ProposedTime[];
  /** The intake call's slots taken from the Hiring Manager's own windows (DC-231): listed first, under *HM availability*. */
  hmWindows?: ProposedTime[] | undefined;
  /** The round's format as the intake fixed it (a round), or null where the Schedule dialog chooses it (the intake call). */
  format: string | null;
  /** Who the proposal was found for: names and role words for the Schedule dialog. */
  attendees: { key: string; name: string; sub?: string | undefined }[];
}

/** The query keys a scheduling write leaves stale: this card, the Workbench, and the V1 records the page also reads. */
const STALE: readonly (readonly unknown[])[] = [
  ["scheduling.open"], ["scheduling.workbench"], ["scheduling.debrief_readiness"], ["round"], ["application"], ["candidates"], ["intake"],
  ["debrief"], ["debriefs"], ["today"], ["packets"],
];

const INTAKE_FORMATS = [
  { value: "teams", label: SCHEDULING_LABELS.virtual },
  { value: "in_person", label: SCHEDULING_LABELS.onsite },
] as const;

/** The card's own empty line before a request exists: his rule for the round that cannot be scheduled yet. */
function notYet(subject: ScheduleSubject): string | null {
  if (subject.kind !== "round") return null;
  if (subject.roundKind === "hm_round") return SCHEDULING_LABELS.beforeStep9;
  if (subject.roundKind === "recruiter_screen") return RULE_LABELS.screenNeedsApplication;
  return null;
}

/**
 * F-158 (DC-237): the page's own facts for *Held offline — continue*, since the scheduling read does not carry them —
 * whether this seat may record the meeting held offline, its parties, and (for a round or the debrief) the step's own
 * held tool; the intake call records through intake.record_held when no `record` is given.
 */
export interface HeldOfflineControl {
  can: boolean;
  attendees: readonly OfflineParty[];
  record?: ((input: OfflineRecord) => Promise<unknown>) | undefined;
  /** The tool whose `offline_reason` limit the dialog's counter reads. */
  tool?: string | undefined;
  onRecorded?: (() => void) | undefined;
}

/** The meeting as the page knows it held: when, and whether offline with its reason. */
export interface HeldState {
  at: string;
  offline: boolean;
  reason: string | null;
}

export interface ScheduleSectionProps {
  subject: ScheduleSubject;
  title?: string | undefined;
  above?: ReactNode;
  /** DC-231: the Hiring Manager's windows as his one line (intake.open `part1_text.availability_line`), shown above
   *  *Propose slots* and in the Schedule dialog of the intake call. */
  availabilityLine?: string | null | undefined;
  heldOffline?: HeldOfflineControl | undefined;
  held?: HeldState | null | undefined;
}

export function ScheduleSection({ subject, title, above, availabilityLine, heldOffline, held }: ScheduleSectionProps) {
  const { me } = useSession();
  const read = useRead(openKey(subject), () => schedulingOpen(openInput(subject)));
  // BRD §3.8 "HM sees authorized results": the Recruiter Screen's scheduling section is absent for the Hiring Manager
  const hmOnly = !!me && me.roles.includes("hm") && !me.roles.some((r) => r === "recruiter" || r === "coordinator" || r === "admin");
  if (subject.kind === "round" && subject.roundKind === "recruiter_screen" && hmOnly) return null;
  const offline = <OfflinePart subject={subject} control={heldOffline} held={held} />;
  const offered = !!heldOffline?.can || !!held?.offline;
  if (read.error) {
    if (!isClientRefusal(read.error)) return <Banner tone="red" live>{errorText(read.error)}</Banner>;
    // no scheduling request (nobody asked for the meeting in the app): the step still moves on with the meeting held
    // offline, so the card carries that control alone — nothing waits on the calendar integration (DC-237)
    const rule = notYet(subject);
    if (!rule && !offered) return null;
    return (
      <Card title={title ?? (subject.kind === "intake" ? templateWord("intake") : undefined)}>
        <Stack gap={12}>
          {rule ? <EmptyState icon="calendar">{rule}</EmptyState> : null}
          {offline}
        </Stack>
      </Card>
    );
  }
  if (!read.data) return <Card title={title} loading={{ lines: 4 }} />;
  return (
    <Body
      key={read.data.scheduling_request_id} request={read.data} subject={subject} title={title} above={above}
      availabilityLine={availabilityLine} offline={offline}
    />
  );
}

/** The query keys a held record leaves stale besides the scheduling ones. */
const HELD_STALE: readonly (readonly unknown[])[] = [...STALE, ["intake.plan_open"], ["plan"], ["timeline"]];

/**
 * *Call held offline — continue* (the intake call) / *Held offline — continue* (a round, the debrief) while the meeting
 * is not held and the seat may record it; once recorded offline, the card reads *Held offline · {reason}*. The
 * scheduling card itself stays: a later booking is still possible, never required.
 */
function OfflinePart({ subject, control, held }: { subject: ScheduleSubject; control?: HeldOfflineControl | undefined; held?: HeldState | null | undefined }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [recorded, setRecorded] = useState<string | null>(null);
  const intake = subject.kind === "intake";
  const reason = held?.offline ? held.reason ?? "" : recorded;
  if (reason !== null) return <Paragraph sm>{HELD_OFFLINE_TEMPLATES.heldOfflineBecause({ reason: reason || "—" })}</Paragraph>;
  if (held || !control?.can) return null;
  const record = control.record ?? (async (input: OfflineRecord) => {
    if (subject.kind !== "intake") return;
    const absent = control.attendees.filter((p) => isRequiredParty(p) && input.present[p.person_id] === false);
    await callTool("intake.record_held", {
      requisition_id: subject.rid,
      held_at: input.held_at,
      attendance: control.attendees.map((p) => ({ person_id: p.person_id, present: input.present[p.person_id] ?? true })),
      absence_exception_reason: absent.length ? input.offline_reason : null,
      held_offline: true,
      offline_reason: input.offline_reason,
    }, { idempotencyKey: newIdempotencyKey() });
  });
  return (
    <>
      <HeldOfflineButton intake={intake} onOpen={() => setOpen(true)} hs={intake ? "sched.offline.intake" : "sched.offline"} />
      {open ? (
        <HeldOfflineDialog
          intake={intake}
          parties={control.attendees}
          tool={control.tool ?? (subject.kind === "round" ? "round.mark_held" : subject.kind === "debrief" ? "debrief.record_attendance" : "intake.record_held")}
          record={record}
          onClose={() => setOpen(false)}
          onRecorded={(why) => {
            setOpen(false);
            setRecorded(why);
            void queryClient.invalidateQueries({ predicate: (q) => HELD_STALE.some((key) => key.every((part, i) => q.queryKey[i] === part)) });
            control.onRecorded?.();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The scheduling card as the whole page, for a seat that schedules a meeting on a page whose other sections it does not
 * read (the Coordinator on a round, the intake call or the debrief): the page header names the meeting and the
 * candidate, and an unknown or unauthorised subject is the canonical not-found — never another record.
 */
export function ScheduleStandalone({ subject, route, body }: { subject: ScheduleSubject; route: string; body?: ReactNode }) {
  const read = useRead(openKey(subject), () => schedulingOpen(openInput(subject)));
  if (read.error) return isClientRefusal(read.error) ? <NotFound route={route} /> : <Banner tone="red" live>{errorText(read.error)}</Banner>;
  const request = read.data;
  if (request && request.requisition_id !== subject.rid) return <NotFound route={route} />;
  const name = request ? [templateWord(request.meeting_type), request.candidate_display_name].filter(Boolean).join(" · ") : "";
  return (
    <McPage>
      <Stack gap={16}>
        <PageHeader title={name} loading={!request} meta={request ? [request.requisition_id, request.requisition_title] : undefined} />
        {request ? (body ?? <ScheduleSection subject={subject} />) : null}
      </Stack>
    </McPage>
  );
}

function Body({ request, subject, title, above, availabilityLine, offline }: {
  request: SchedulingOpenOutput;
  subject: ScheduleSubject;
  title?: string | undefined;
  above?: ReactNode;
  availabilityLine?: string | null | undefined;
  offline?: ReactNode;
}) {
  const { manifest, me } = useSession();
  const queryClient = useQueryClient();
  const manager = acts(manifest, me, "scheduling.book");
  const requester = !manager && acts(manifest, me, "scheduling.request_reschedule");
  const tz = request.event_tz;
  const meetings = meetingsOf(request, subject);
  const booked = meetings.find((m) => m.state === "booked" || m.state === "held_tentative") ?? null;
  const ended = request.status === "completed" || request.status === "cancelled";
  const blocked = request.blocked_reason ? { reason: SCHEDULING_LABELS.panelDeclined } : undefined;
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [chosen, setChosen] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const [recorded, setRecorded] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [answering, setAnswering] = useState<"accept" | "decline" | undefined>(undefined);
  const [windowFrom, setWindowFrom] = useState("");
  const [windowTo, setWindowTo] = useState("");
  const [format, setFormat] = useState<string>("teams");
  const [itineraryText, setItineraryText] = useState<string | undefined>(undefined);
  /** The reason this viewer just gave for a reschedule request (the read carries no reason text: it is the receipt's). */
  const [asked, setAsked] = useState<string | null>(null);
  const win = proposal ? windowOf(proposal.slots) : null;
  const avail = useRead(
    ["scheduling.availability", request.scheduling_request_id, win, subject.kind === "round" ? subject.roundDefId ?? null : null],
    () => schedulingAvailability({
      scheduling_request_id: request.scheduling_request_id,
      earliest: win!.earliest,
      latest: win!.latest,
      round_def_id: subject.kind === "round" ? subject.roundDefId ?? null : null,
    }),
    { enabled: manager && win !== null },
  );
  const attendees: readonly AttendeeAvailabilityOut[] = avail.data?.attendees ?? [];
  const slot = proposal?.slots.find((s) => s.start === chosen) ?? null;
  const proposingNow = manager && !ended && (!booked || rescheduling);
  const withCandidate = subject.kind === "round" && (subject.roundKind === "recruiter_screen" || subject.roundKind === "hm_round");

  const done = async () => {
    setOpen(null);
    setProposal(null);
    setChosen("");
    setRescheduling(false);
    setRecorded(true);
    await queryClient.invalidateQueries({ predicate: (q) => STALE.some((key) => key.every((part, i) => q.queryKey[i] === part)) });
  };
  const call = <T,>(name: string, input: unknown, reason?: string) =>
    callTool<T>(name, input, { idempotencyKey: newIdempotencyKey(), ...(reason ? { reason } : {}) });
  const ref = subject.kind === "round"
    ? { requisition_id: subject.rid, application_id: subject.aid, round_kind: subject.roundKind, round_def_id: subject.roundDefId ?? null }
    : null;

  const propose = async () => {
    setProposing(true);
    setProblem(null);
    setRecorded(false);
    try {
      if (subject.kind === "round") {
        const windows = windowFrom && windowTo ? [{ start: new Date(windowFrom).toISOString(), end: new Date(windowTo).toISOString() }] : [];
        const out = await call<RoundProposeOutput>("round.propose", { ...ref, candidate_windows: windows, ...sequenceWindow(request, ref?.round_def_id) });
        setProposal({
          slots: out.slots.map((s) => ({ start: s.start, end: s.end })),
          format: formatWord(out.format) ?? null,
          attendees: out.attendees.map((a) => ({ key: String(a["person_id"]), name: String(a["name"] ?? ""), sub: ROLE_WORD[String(a["role"])] })),
        });
        setChosen(out.slots[0]?.start ?? "");
      } else {
        const out = subject.kind === "intake"
          ? await call<IntakeScheduleSessionOutput>("intake.schedule_session", { requisition_id: subject.rid })
          : await call<DebriefScheduleOutput>(booked ? "debrief.reschedule" : "debrief.schedule", { requisition_id: subject.rid, cycle_id: subject.cid });
        const all = (out.proposals as { start: string; end: string; source?: string }[]).map((p) => ({
          start: String(p.start), end: String(p.end), hm: p.source === "hm_window",
        }));
        // DC-231: the Hiring Manager's own windows come first (the server keeps those the required attendees are free
        // for), under *HM availability*; the free/busy proposals follow
        const hmWindows = all.filter((p) => p.hm).map(({ start, end }) => ({ start, end }));
        const others = all.filter((p) => !p.hm).map(({ start, end }) => ({ start, end }));
        setProposal({ slots: [...hmWindows, ...others], hmWindows, format: subject.kind === "intake" ? null : formatWord("virtual"), attendees: [] });
        setChosen(hmWindows[0]?.start ?? others[0]?.start ?? "");
      }
    } catch (error) {
      setProblem(errorText(error));
    } finally {
      setProposing(false);
    }
  };

  /** Book the chosen slot through the page's own tool (a moved round is booked again the same way). */
  const book = async (start: string, reason?: string) => {
    if (subject.kind === "round") {
      const out = await call<RoundBookOutput>("round.book", { ...ref, start }, reason);
      setItineraryText(out.itinerary ?? undefined);
    } else if (subject.kind === "intake") {
      // DC-231: the call is as long as the slot chosen (the HM's window length); with no slot length the server takes
      // the HM's window length itself — never a fixed hour
      const picked = proposal?.slots.find((s) => s.start === start);
      const minutes = picked ? Math.round((Date.parse(picked.end) - Date.parse(picked.start)) / 60000) : 0;
      await call("intake.schedule_session", {
        requisition_id: subject.rid, start, ...(minutes >= 15 && minutes <= 240 ? { duration_minutes: minutes } : {}), format,
      }, reason);
    } else {
      await call(booked ? "debrief.reschedule" : "debrief.schedule", { requisition_id: subject.rid, cycle_id: subject.cid, start, duration_minutes: 60 }, reason);
    }
  };

  /** Reschedule: a booked round is moved and booked at the new time in one call (its event kept for the update), the
   *  conflicts checked again there first, so a refusal leaves it booked where it was; the intake call and the debrief
   *  move their meeting in one call too. */
  const reschedule = async (reason: string, acceptConflicts: boolean) => {
    if (!slot) return;
    if (subject.kind === "round" && booked) {
      const out = await call<RoundBookOutput>("scheduling.reschedule_round", {
        ...ref, start: slot.start, cause: causeOf(me?.roles ?? []), reason, accept_conflicts: acceptConflicts,
      });
      setItineraryText(out.itinerary ?? undefined);
      return;
    }
    await book(slot.start, reason);
  };

  const respond = async (response: "accepted" | "declined") => {
    const mine = meetings.find((m) => m.attendees.some((a) => a.person_id === me?.person_id));
    if (!mine) return;
    setAnswering(response === "accepted" ? "accept" : "decline");
    setProblem(null);
    try {
      await call("scheduling.respond", { meeting_event_id: mine.meeting_event_id, response });
      await done();
    } catch (error) {
      setProblem(errorText(error));
    } finally {
      setAnswering(undefined);
    }
  };

  const conflicts: ConflictItem[] = meetings.flatMap((m) => m.conflicts.map((c) => ({ name: ROLE_WORD[c.role] ?? c.role, reason: conflictWord(c.kind) })));
  // one Final Interview Loop round's page reads its own round: Scheduled once that round is booked, whatever the rest
  // of the loop still waits for (the loop's own status is on Candidates > Schedule rounds)
  const roundOnly = subject.kind === "round" && !!subject.roundDefId;
  const statusKey = roundOnly ? (booked ? "scheduled" : request.status === "scheduled" ? "requested" : request.status) : request.status;
  const status = statusWord(statusKey);
  const grid = slot && attendees.length ? dayGrid(slot, tz, attendees) : null;
  // A18: the chosen slot breaks a calendar the published conflict policy refuses without a reason — the dialogs ask for
  // *Conflict* and the Reason exactly when booking would (a `warn` conflict is shown on the slot and asks for nothing)
  const slotConflicted = !!slot && blockingAt(slot, attendees, avail.data?.conflict_policy ?? {}).length > 0;

  return (
    <Card
      title={title ?? templateWord(request.meeting_type)}
      pills={<Pill tone={toneOf(status as StatusWord)}>{status}</Pill>}
      
    >
      <Stack gap={16}>
        {above}
        {recorded ? <Recorded live /> : null}
        {problem ? <Banner tone="red" live>{problem}</Banner> : null}
        <Journey label={templateWord(request.meeting_type)} sm steps={journey(statusKey)} />
        {meetings.map((meeting) => (
          <MeetingEvent
            key={meeting.meeting_event_id}
            title={meeting.round_label ?? templateWord(request.meeting_type)}
            items={withTeams(itinerary(meeting), meeting)}
            meetingId={meeting.teams_meeting_id ?? meeting.provider_event_id ?? undefined}
            sync={syncOf(meeting, request.syncs ?? [], tz, { canRetry: false, canRecover: false, retrying: false, onRetry: () => undefined, onRecover: () => undefined })}
            itineraryText={itineraryText}
            copyInWords
            canReschedule={manager && !ended && meeting.meeting_event_id === booked?.meeting_event_id && !rescheduling}
            onReschedule={() => { setRescheduling(true); setRecorded(false); }}
            canCancel={manager && !ended && subject.kind === "round"}
            onCancel={() => setOpen("cancel")}
            hs={{ reschedule: "sched.page.reschedule", cancel: "sched.page.cancel" }}
          >
            {meeting.organizer ? (
              // DC-240: the Coordinator who scheduled it is named above the responses with the Coordinator tag and is
              // never one of them
              <Flex gap={8} align="center">
                <Tag>{ROLE_LABELS.coordinator}</Tag>
                <span>{meeting.organizer.name}</span>
              </Flex>
            ) : null}
            <Responses
              items={answerRows(meeting, me?.person_id, request.meeting_type)}
              onAccept={() => void respond("accepted")}
              onDecline={() => void respond("declined")}
              busy={answering}
              hs={{ accept: "sched.accept", decline: "sched.decline" }}
            />
          </MeetingEvent>
        ))}
        {conflicts.length > 0 ? <Conflicts items={conflicts}  /> : null}
        {request.status === "reschedule_requested" ? <Paragraph sm>{rescheduleLine(request, tz, asked)}</Paragraph> : null}
        {requester && booked && !ended && request.status !== "reschedule_requested" ? (
          <div>
            <Button tone="ghost" icon="refresh" onClick={() => setOpen("request")} >{RESCHEDULE_REQUEST_LABELS.requestAReschedule}</Button>
          </div>
        ) : null}
        {offline}
        {proposingNow ? (
          <Stack gap={12}>
            {withCandidate ? (
              <Flex gap={8} wrap>
                <Field label={`${SCHEDULING_LABELS.candidateAvailability} · ${tz}`} optional>
                  <Input type="datetime-local" value={windowFrom} onChange={(event) => setWindowFrom(event.target.value)}  />
                </Field>
                <Field label={`${SCHEDULING_LABELS.candidateAvailability} · ${tz}`} optional>
                  <Input type="datetime-local" value={windowTo} onChange={(event) => setWindowTo(event.target.value)}  />
                </Field>
              </Flex>
            ) : null}
            {subject.kind === "intake" && availabilityLine ? (
              <Stack gap={4}>
                <Paragraph sm><b>{INTAKE_SCHEDULING_LABELS.hmAvailability}</b></Paragraph>
                <Paragraph sm>{availabilityLine}</Paragraph>
              </Stack>
            ) : null}
            <div>
              <Button tone="ghost" icon="refresh" busy={proposing} blocked={blocked} onClick={() => void propose()} >
                {ACTION_LABELS.proposeSlots}
              </Button>
            </div>
            {proposal ? (
              <Stack gap={12}>
                {proposal.hmWindows?.length ? (
                  <Stack gap={8}>
                    <Paragraph sm><b>{INTAKE_SCHEDULING_LABELS.hmAvailability}</b></Paragraph>
                    <Slots items={slotItems(proposal.hmWindows, tz, attendees)} value={chosen} onChange={setChosen} blocked={blocked}  />
                  </Stack>
                ) : null}
                <Paragraph sm>{proposedCount(proposal.slots.length - (proposal.hmWindows?.length ?? 0))}</Paragraph>
                {proposal.slots.length > (proposal.hmWindows?.length ?? 0) ? (
                  <Slots
                    items={slotItems(proposal.slots.slice(proposal.hmWindows?.length ?? 0), tz, attendees)}
                    value={chosen} onChange={setChosen} blocked={blocked} 
                  />
                ) : null}
                {grid ? <Availability columns={grid.columns} rows={grid.rows} selected={grid.selected}  /> : null}
                {slot ? (
                  <TimeZone options={[{ value: tz, label: tz }]} value={tz} mode="view" event={`${formatDateTime(slot.start, tz)} · ${tz}`} />
                ) : null}
                <div>
                  <Button
                    tone="primary"
                    blocked={blocked ?? (slot ? undefined : { reason: SCHEDULING_LABELS.proposedSlots })}
                    onClick={() => setOpen(rescheduling ? "reschedule" : "schedule")}
                    
                  >
                    {rescheduling ? ACTION_LABELS.reschedule : ACTION_LABELS.schedule}
                  </Button>
                </div>
              </Stack>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      {open === "schedule" && slot ? (
        <ScheduleDialog
          when={`${formatDateTime(slot.start, tz)} · ${tz}`}
          attendees={proposal?.attendees.length ? proposal.attendees : attendees.map((a) => ({ key: a.person_id, name: ROLE_WORD[a.role] ?? a.role, sub: a.timezone }))}
          format={subject.kind === "intake" ? { value: format, options: INTAKE_FORMATS, onChange: setFormat } : undefined}
          fixedFormat={subject.kind === "intake" ? undefined : proposal?.format}
          conflicted={slotConflicted}
          note={subject.kind === "intake" && availabilityLine ? [INTAKE_SCHEDULING_LABELS.hmAvailability, availabilityLine] : undefined}
          onSchedule={(reason) => book(slot.start, reason)}
          onClose={() => setOpen(null)}
          onDone={() => void done()}
        />
      ) : null}
      {open === "reschedule" && slot && booked ? (
        <SlotRescheduleDialog
          original={booked.starts_at ? `${formatDateTime(booked.starts_at, tz)} · ${tz}` : "—"}
          revised={`${formatDateTime(slot.start, tz)} · ${tz}`}
          conflicted={slotConflicted}
          onReschedule={reschedule}
          onClose={() => setOpen(null)}
          onDone={() => void done()}
        />
      ) : null}
      {open === "cancel" && ref ? (
        <ReasonCancelDialog
          told={[...new Set(meetings.flatMap((m) => m.attendees.map((a) => a.name)))]}
          onCancel={async (reason) => { await call("round.cancel", { ...ref, reason }); }}
          onClose={() => setOpen(null)}
          onDone={() => void done()}
        />
      ) : null}
      {open === "request" && booked ? (
        <RequestDialog
          onRequest={async (reason) => {
            await call("scheduling.request_reschedule", {
              scheduling_request_id: request.scheduling_request_id, meeting_event_id: booked.meeting_event_id, cause: causeOf(me?.roles ?? []), reason,
            });
            setAsked(reason);
          }}
          onClose={() => setOpen(null)}
          onDone={() => void done()}
        />
      ) : null}
    </Card>
  );
}

/** Propose slots → Availability → Create Outlook event + Teams link, as far as the request has come. */
function journey(status: string) {
  const at = ({ requested: 0, in_progress: 1, awaiting_candidate: 1, awaiting_interviewer: 1, scheduled: 3, reschedule_requested: 3, completed: 3 } as Record<string, number>)[status] ?? -1;
  const state = (index: number) => (at < 0 ? "lock" as const : index < at ? "done" as const : index === at ? "active" as const : "next" as const);
  return [
    { label: ACTION_LABELS.proposeSlots, state: state(0) },
    { label: SCHEDULING_JOURNEY_LABELS.availability, state: state(1) },
    { label: SCHEDULING_JOURNEY_LABELS.createEvent, state: state(2) },
  ];
}

/** The response rows: the viewer's own row answers (Accept · Decline) while it is pending; on a debrief the
 *  Coordinator's row says *No vote* ("Coordinator schedules but has no vote."). */
function answerRows(meeting: MeetingOut, personId: string | undefined, meetingType: string) {
  return responses(meeting).map((row) => ({
    ...row,
    // the organizer's row is never an invitation to answer: the Coordinator schedules and does not respond or vote
    own: row.key === personId && row.status === "pending" && !row.coordinator,
    sub: meetingType === "debrief" && row.coordinator ? `${row.sub ?? ""} · ${DEBRIEF_ROOM_LABELS.noVote}` : row.sub,
  }));
}

/** DC-241: *Reschedule requested by {name} · {reason} · {when}* from the request's latest ask (its history row names
 *  who and when; the reason is the viewer's own when they just asked, else not in the read). */
export function rescheduleLine(request: SchedulingOpenOutput, tz: string, ownReason: string | null): string {
  const ask = [...(request.history ?? [])].reverse().find((h) => h.kind === "reschedule_requested");
  const detailReason = ask && typeof ask.detail["reason"] === "string" ? String(ask.detail["reason"]) : null;
  return RESCHEDULE_REQUEST_TEMPLATES.requestedBy({
    name: ask?.actor_name ?? "—",
    reason: detailReason ?? ownReason ?? "—",
    when: ask ? formatDateTime(ask.at, tz) : "—",
  });
}
